import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Mic, Square, Loader } from 'lucide-react';
import './VoiceInput.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_SECONDS = 120;

const pickMime = () => {
  if (typeof MediaRecorder === 'undefined') return '';
  return ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'].find(t => MediaRecorder.isTypeSupported(t)) || '';
};

// Tap to record, tap again to stop → Whisper transcript via backend.
export const VoiceInput = ({ onTranscript, onError, beforeStart, disabled = false, size = 'md', testId = 'voice-mic-btn' }) => {
  const [status, setStatus] = useState('idle'); // idle | recording | transcribing
  const [seconds, setSeconds] = useState(0);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  const supported = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== 'undefined';

  const cleanup = () => {
    clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    recorderRef.current = null;
  };

  useEffect(() => cleanup, []);

  const upload = async (blob, mime) => {
    setStatus('transcribing');
    try {
      const ext = mime.includes('mp4') ? 'mp4' : mime.includes('ogg') ? 'ogg' : 'webm';
      const form = new FormData();
      form.append('audio', blob, `voice.${ext}`);
      const res = await axios.post(`${API_URL}/api/intelligence/voice/transcribe`, form);
      onTranscript?.(res.data.text, res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      onError?.(typeof detail === 'string' ? detail : 'Voice transcription failed. Please try again.', err.response?.status);
    } finally {
      setStatus('idle');
      setSeconds(0);
    }
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMime();
      const recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const type = recorder.mimeType || mime || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type });
        cleanup();
        if (blob.size < 1000) { setStatus('idle'); setSeconds(0); onError?.('Recording was too short. Tap the mic and speak for a moment.'); return; }
        upload(blob, type);
      };
      recorderRef.current = recorder;
      recorder.start(250);
      setSeconds(0);
      setStatus('recording');
      timerRef.current = setInterval(() => {
        setSeconds(s => {
          if (s + 1 >= MAX_SECONDS) { stop(); return s; }
          return s + 1;
        });
      }, 1000);
    } catch (err) {
      cleanup();
      onError?.(err?.name === 'NotAllowedError' ? 'Microphone access was blocked. Allow the mic in your browser to use voice.' : 'Could not access the microphone.');
    }
  };

  const stop = () => {
    clearInterval(timerRef.current);
    const r = recorderRef.current;
    if (r && r.state !== 'inactive') r.stop();
  };

  const toggle = () => {
    if (disabled || status === 'transcribing') return;
    if (status === 'recording') { stop(); return; }
    if (beforeStart && beforeStart() === false) return;
    start();
  };

  if (!supported) return null;

  const label = status === 'recording' ? 'Stop recording' : status === 'transcribing' ? 'Transcribing…' : 'Speak your question';
  const mmss = `${String(Math.floor(seconds / 60)).padStart(1, '0')}:${String(seconds % 60).padStart(2, '0')}`;

  return (
    <div className={`voice-input voice-${size} voice-${status}`} data-testid="voice-input">
      {status === 'recording' && (
        <div className="voice-live" data-testid="voice-recording-indicator">
          <span className="voice-wave"><i /><i /><i /><i /></span>
          <span className="voice-timer">{mmss}</span>
        </div>
      )}
      <button
        type="button"
        className="voice-mic-btn"
        onClick={toggle}
        disabled={disabled || status === 'transcribing'}
        aria-label={label}
        title={label}
        aria-pressed={status === 'recording'}
        data-testid={testId}
        data-status={status}
      >
        {status === 'recording' ? <Square size={size === 'lg' ? 20 : 16} /> : status === 'transcribing' ? <Loader size={size === 'lg' ? 22 : 18} className="voice-spin" /> : <Mic size={size === 'lg' ? 22 : 18} />}
      </button>
    </div>
  );
};

export const VoiceChip = ({ onDismiss, testId = 'voice-transcribed-chip' }) => (
  <div className="voice-chip" data-testid={testId}>
    <Mic size={11} />
    <span>Transcribed from voice — edit if needed</span>
    {onDismiss && <button type="button" onClick={onDismiss} aria-label="Dismiss" className="voice-chip-x" data-testid={`${testId}-dismiss`}>×</button>}
  </div>
);

export default VoiceInput;
